import React, { useState } from 'react'

import { EDH_FORMATS } from 'types/deck'

import { useAppSelector } from 'app-redux/hooks'

import PhatDropdown from 'components/formElements/PhatDropdown'
import QuantityInput from 'components/card/cardForm/quantityInput'
import Row, { GroupBy } from 'components/deckPage/RightPanel/Probability/Row'
import SelectingAndClause, { AndClause, ProbabilityLogic, defaultAndClasue } from './SelectingAndClause'

import {
  cumulativeHypergeometricProbability,
  hypergeometricProbability,
} from 'components/deckPage/RightPanel/Probability/realMath'

import styles from './probability.module.scss'

const Probability = () => {
  const cardMap = useAppSelector(state => state.deck.cardMap)
  const categories = useAppSelector(state => state.deck.categories)
  const format = useAppSelector(state => state.deck.format)

  const [successesInSample, setSuccessesInSample] = useState(1)
  const [cardsDrawn, setCardsDrawn] = useState(7)
  const [logic, setLogic] = useState<ProbabilityLogic>('atLeast')
  const [type, setType] = useState<GroupBy>(EDH_FORMATS.includes(format) ? 'categories' : 'cardName')
  const [andClauses, setAndClauses] = useState(new Array<AndClause>())

  const deck = Object.values(cardMap)
  const probabilities: Record<string, number> = {}

  let deckSize = 0

  for (const card of deck) {
    const primaryCategory = categories[card.categories[0]]

    // prettier-ignore
    if (primaryCategory?.isPremier || !primaryCategory?.includedInDeck || primaryCategory?.name === 'Sideboard') continue

    const datum = new Array<string | number>()

    if (type === 'cardName') datum.push(card.name)
    else if (type === 'types') datum.push(...card.types)
    else if (type === 'manaValue') datum.push(card.cmc)
    else if (type === 'categories') datum.push(...card.categories)
    else if (type === 'subTypes') datum.push(...card.subTypes)
    else if (type === 'keywords') datum.push(...card.keywords)

    deckSize += card.qty

    datum.forEach(data => {
      const currentCount = probabilities[data]

      if (currentCount) probabilities[data] = currentCount + card.qty
      else probabilities[data] = card.qty
    })
  }

  return (
    <div className={styles.container}>
      <div className={styles.controls}>
        <div className={styles.primaryControls}>
          <label>Probability of drawing</label>
          <PhatDropdown
            value={logic}
            options={[
              { label: 'At least', onClick: () => setLogic('atLeast'), id: 'atLeast' },
              { label: 'Exactly', onClick: () => setLogic('exactly'), id: 'exactly' },
            ]}
            className={styles.logicControls}
          />
          <QuantityInput
            noLabel
            quantity={successesInSample}
            dontAllowZero
            onQuantityUpdate={setSuccessesInSample}
            className={styles.quantityInput}
          />
          <label>card(s) by</label>
          <PhatDropdown
            className={styles.typeSelector}
            value={type}
            options={[
              { label: 'Card Name', onClick: () => setType('cardName'), id: 'cardName' },
              { label: 'Categories', onClick: () => setType('categories'), id: 'categories' },
              { label: 'Types', onClick: () => setType('types'), id: 'types' },
              { label: 'Sub Types', onClick: () => setType('subTypes'), id: 'subTypes' },
              { label: 'Mana Value', onClick: () => setType('manaValue'), id: 'manaValue' },
              { label: 'Keywords', onClick: () => setType('keywords'), id: 'keywords' },
            ]}
          />
          <label>having drawn</label>
          <QuantityInput
            noLabel
            quantity={cardsDrawn}
            dontAllowZero
            onQuantityUpdate={setCardsDrawn}
            className={styles.quantityInput}
          />
          <label>card(s)</label>
        </div>

        {andClauses.map((clause, index) => (
          <>
            <label className={styles.andLabel}>and</label>
            <SelectingAndClause
              key={index}
              value={clause}
              onCancel={() => setAndClauses(andClauses.filter((_, i) => i !== index))}
              onUpdate={clause => {
                const updatedAndClauses = [...andClauses]
                updatedAndClauses[index] = clause

                setAndClauses(updatedAndClauses)
              }}
            />
          </>
        ))}

        {/* TODO - Add back later, needs real math person */}
        {/* <div className={styles.addAndClauseButton}>
          <PhatButton icon="circle-plus" color="blue" onClick={() => setAndClauses([...andClauses, defaultAndClasue])}>
            And logic
          </PhatButton>
        </div> */}
      </div>

      <div className={styles.table}>
        <div className={`${styles.row} ${styles.header}`}>
          <span className={styles.type}>
            {type === 'cardName' && 'Card Name'}
            {type === 'types' && 'Types'}
            {type === 'manaValue' && 'Mana Value'}
            {type === 'categories' && 'Categories'}
            {type === 'keywords' && 'Keywords'}
          </span>
          <span className={styles.count}>Qty</span>
          <span className={styles.odds}>Odds</span>
        </div>
        {Object.keys(probabilities)
          .sort((a, b) => (a > b ? 1 : -1))
          .map(data => {
            const quantityInDeck = probabilities[data]
            const probaility =
              logic === 'atLeast'
                ? cumulativeHypergeometricProbability(deckSize, quantityInDeck, cardsDrawn, successesInSample)
                : hypergeometricProbability(deckSize, quantityInDeck, cardsDrawn, successesInSample)

            // Need math guy to fix this
            // const operations: ProbabilityOperator[] = [
            //   {
            //     logic,
            //     appearancesInDeck: quantityInDeck,
            //     successInDraw: successesInSample,
            //   },
            //   ...andClauses.map(clause => ({
            //     logic: clause.logic,
            //     appearancesInDeck: clause.successes,
            //     successInDraw: clause.successesInSample,
            //   })),
            // ]

            // const probaility = exactlyProbabilityAnded(deckSize, cardsDrawn, operations)
            const odds = Math.round(probaility * 100)

            return <Row key={data} data={data} odds={odds} quantity={quantityInDeck} type={type} />
          })}
      </div>
    </div>
  )
}

export default Probability
